import dagre from "dagre";

const NODE_DIMENSIONS = {
  data: { width: 220, height: 100 },
  comparison: { width: 200, height: 110 },
  arithmetic: { width: 200, height: 110 },
  logic: { width: 260, height: 120 },
  output: { width: 180, height: 72 },
};

const DATA_STEP_TYPES = ["OBTER_GRANDEZA", "OBTER_VARIAVEL_CONTRATO", "OBTER_PARAMETRO"];

// A config pode ter objetos aninhados (a definição de grandeza); uma cópia rasa
// faria os nós compartilharem a mesma definição.
function cloneConfig(config) {
  return JSON.parse(JSON.stringify(config || {}));
}

export function flowNodeTypeFromPasso(tipoPasso) {
  if (DATA_STEP_TYPES.includes(tipoPasso)) return "data";
  if (tipoPasso === "COMPARAR") return "comparison";
  if (tipoPasso === "ARITMETICO") return "arithmetic";
  if (tipoPasso === "LOGICO") return "logic";
  return "data";
}

export function createFlowNodeId(passo) {
  return passo._uid || `node-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

export function createEmptyOutputNode(label) {
  return {
    id: "flow-output",
    type: "output",
    position: { x: 0, y: 0 },
    data: { label: label || "resultado" },
  };
}

export function passosToFlow(passos, outputLabel) {
  const safePassos = Array.isArray(passos) ? passos : [];
  const nodes = safePassos.map((passo, idx) => ({
    id: createFlowNodeId(passo),
    type: flowNodeTypeFromPasso(passo.tipo_passo),
    position: { x: idx * 280, y: 80 },
    data: {
      _uid: passo._uid || createFlowNodeId(passo),
      tipo_passo: passo.tipo_passo,
      config: cloneConfig(passo.config),
      saida_alias: passo.saida_alias || "",
      ordem: passo.ordem != null ? passo.ordem : idx,
    },
  }));

  const outputNode = createEmptyOutputNode(outputLabel);
  nodes.push(outputNode);

  const aliasToNodeId = {};
  nodes.forEach((node) => {
    if (node.type === "output") return;
    const alias = String(node.data.saida_alias || "").trim().toLowerCase();
    if (alias) aliasToNodeId[alias] = node.id;
  });

  const edges = [];
  safePassos.forEach((passo) => {
    const targetId = createFlowNodeId(passo);
    const cfg = passo.config || {};
    edges.push(...edgesFromPassoConfig(passo.tipo_passo, cfg, aliasToNodeId, targetId));
  });

  if (safePassos.length) {
    const sorted = [...safePassos].sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0));
    const lastPasso = sorted[sorted.length - 1];
    const lastNodeId = createFlowNodeId(lastPasso);
    edges.push({
      id: `e-${lastNodeId}-output`,
      source: lastNodeId,
      target: outputNode.id,
      sourceHandle: "out",
      targetHandle: "in",
    });
  }

  return layoutFlowGraph(nodes, edges);
}

function edgesFromPassoConfig(tipoPasso, cfg, aliasToNodeId, targetId) {
  const edges = [];
  const link = (alias, targetHandle) => {
    const key = String(alias || "").trim().toLowerCase();
    const sourceId = aliasToNodeId[key];
    if (!sourceId) return;
    edges.push({
      id: `e-${sourceId}-${targetId}-${targetHandle}`,
      source: sourceId,
      target: targetId,
      sourceHandle: "out",
      targetHandle,
    });
  };

  if (tipoPasso === "COMPARAR" || tipoPasso === "ARITMETICO") {
    link(cfg.left_alias, "left");
    link(cfg.right_alias, "right");
    return edges;
  }

  if (tipoPasso !== "LOGICO") return edges;

  if (cfg.operador === "NOT") {
    link(cfg.operand_alias || cfg.left_alias, "in");
    return edges;
  }

  if ((cfg.operador === "AND" || cfg.operador === "OR") && Array.isArray(cfg.operands)) {
    cfg.operands.forEach((alias, idx) => link(alias, `in-${idx}`));
    return edges;
  }

  link(cfg.left_alias, "left");
  link(cfg.right_alias, "right");
  return edges;
}

export function flowToPassos(nodes, edges) {
  const passoNodes = (nodes || []).filter((n) => n.type !== "output");
  const nodesById = {};
  passoNodes.forEach((n) => {
    nodesById[n.id] = n;
  });

  const sorted = topologicalSortPassoNodes(passoNodes, edges || []);
  return sorted.map((node, idx) => {
    const data = { ...node.data, config: cloneConfig(node.data.config) };
    syncPassoConfigFromEdges(node.id, data, edges || [], nodesById);
    return {
      _uid: data._uid || node.id,
      tipo_passo: data.tipo_passo,
      config: data.config,
      saida_alias: String(data.saida_alias || "").trim(),
      ordem: idx,
    };
  });
}

function syncPassoConfigFromEdges(nodeId, data, edges, nodesById) {
  const incoming = edges.filter((e) => e.target === nodeId);
  const aliasOf = (edge) => {
    const src = nodesById[edge.source];
    return src ? String(src.data.saida_alias || "").trim().toLowerCase() : null;
  };

  switch (data.tipo_passo) {
    case "COMPARAR":
    case "ARITMETICO": {
      const left = incoming.find((e) => e.targetHandle === "left");
      const right = incoming.find((e) => e.targetHandle === "right");
      if (left) data.config.left_alias = aliasOf(left);
      if (right) data.config.right_alias = aliasOf(right);
      break;
    }
    case "LOGICO": {
      const op = data.config.operador;
      if (op === "NOT") {
        const inp = incoming.find((e) => e.targetHandle === "in");
        if (inp) data.config.operand_alias = aliasOf(inp);
      } else if (op === "AND" || op === "OR") {
        data.config.operands = incoming
          .filter((e) => String(e.targetHandle || "").startsWith("in-"))
          .sort((a, b) => {
            const ai = Number(String(a.targetHandle).split("-")[1] || 0);
            const bi = Number(String(b.targetHandle).split("-")[1] || 0);
            return ai - bi;
          })
          .map(aliasOf)
          .filter(Boolean);
      } else {
        const left = incoming.find((e) => e.targetHandle === "left");
        const right = incoming.find((e) => e.targetHandle === "right");
        if (left) data.config.left_alias = aliasOf(left);
        if (right) data.config.right_alias = aliasOf(right);
      }
      break;
    }
    default:
      break;
  }
}

function topologicalSortPassoNodes(nodes, edges) {
  const ids = nodes.map((n) => n.id);
  const inDegree = {};
  const adj = {};
  ids.forEach((id) => {
    inDegree[id] = 0;
    adj[id] = [];
  });

  edges.forEach((e) => {
    if (e.target === "flow-output") return;
    if (!ids.includes(e.source) || !ids.includes(e.target)) return;
    adj[e.source].push(e.target);
    inDegree[e.target] += 1;
  });

  const queue = ids
    .filter((id) => inDegree[id] === 0)
    .sort((a, b) => nodeSortKey(nodes, a) - nodeSortKey(nodes, b));

  const sorted = [];
  while (queue.length) {
    const id = queue.shift();
    sorted.push(nodes.find((n) => n.id === id));
    adj[id].forEach((next) => {
      inDegree[next] -= 1;
      if (inDegree[next] === 0) {
        queue.push(next);
        queue.sort((a, b) => nodeSortKey(nodes, a) - nodeSortKey(nodes, b));
      }
    });
  }

  if (sorted.length < nodes.length) {
    return [...nodes].sort((a, b) => nodeSortKey(nodes, a.id) - nodeSortKey(nodes, b.id));
  }
  return sorted;
}

function nodeSortKey(nodes, id) {
  const node = nodes.find((n) => n.id === id);
  return node && node.position ? node.position.x : 0;
}

export function layoutFlowGraph(nodes, edges) {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: "LR", nodesep: 60, ranksep: 100, marginx: 40, marginy: 40 });

  (nodes || []).forEach((node) => {
    const dim = NODE_DIMENSIONS[node.type] || NODE_DIMENSIONS.data;
    g.setNode(node.id, { width: dim.width, height: dim.height });
  });

  (edges || []).forEach((edge) => {
    if (g.hasNode(edge.source) && g.hasNode(edge.target)) {
      g.setEdge(edge.source, edge.target);
    }
  });

  dagre.layout(g);

  const laidOut = (nodes || []).map((node) => {
    const pos = g.node(node.id);
    if (!pos) return node;
    const dim = NODE_DIMENSIONS[node.type] || NODE_DIMENSIONS.data;
    return {
      ...node,
      position: {
        x: pos.x - dim.width / 2,
        y: pos.y - dim.height / 2,
      },
    };
  });

  return { nodes: laidOut, edges: edges || [] };
}

export function defaultPassoConfig(tipo) {
  switch (tipo) {
    case "OBTER_GRANDEZA":
      // A grandeza é definida no próprio passo; o id é resolvido ao salvar.
      return { grandeza: { id_variavel: null, funcao_agregacao: "SOMA", id_agrupamento: null } };
    case "OBTER_VARIAVEL_CONTRATO":
      return { id_variavel: null };
    case "OBTER_PARAMETRO":
      return { param_key: "", valor_constante: null };
    case "COMPARAR":
      return { operador: "GTE", left_alias: null, right_alias: null };
    case "ARITMETICO":
      return { operador: "DIV", left_alias: null, right_alias: null };
    case "LOGICO":
      return { operador: "AND", operands: [] };
    default:
      return {};
  }
}

export function createFlowNodeFromTipo(tipoPasso, index) {
  const _uid = `passo-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  return {
    id: _uid,
    type: flowNodeTypeFromPasso(tipoPasso),
    position: { x: 80 + index * 40, y: 80 + index * 30 },
    data: {
      _uid,
      tipo_passo: tipoPasso,
      config: defaultPassoConfig(tipoPasso),
      saida_alias: "",
      ordem: index,
    },
  };
}

export function getNodeAccent(type) {
  const map = {
    data: "#1976d2",
    comparison: "#43a047",
    arithmetic: "#f9a825",
    logic: "#fb8c00",
    output: "#5e35b1",
  };
  return map[type] || "#757575";
}
